"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { readHomePromptStash } from "@/lib/home-prompt-stash";
import { readCloneIntent } from "@/lib/home-clone-stash";
import { GoogleSignInButton } from "@/components/auth/google-sign-in-button";
import { FacebookSignInButton } from "@/components/auth/facebook-sign-in-button";
import { LinkedInSignInButton } from "@/components/auth/linkedin-sign-in-button";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { isValidEmailStructure } from "@/lib/validation/email";
import { getEmailSuggestion } from "@/lib/validation/email-suggestion";
import { ArrowLeft, Check, Eye, EyeOff, X } from "lucide-react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";

const fontTight = "font-[family-name:var(--font-inter-tight),sans-serif]";

// Mirrors signup-modal.tsx — Figma node 2350:42030 / 2350:42097 tokens:
// 1px border-/-strong, rounded-full, gap-2 icon+text, h-12, Rubik Medium
// 16/24 text-/-primary. Dark-mode hexes are the dark-resolved values for
// each token (border / strong #334155, text / primary #f1f5f9), pulled
// from Figma node 1696:16735's variable defs.
const socialBtnClass =
  "flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#CBD5E1] bg-white font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-[#1A1A1B] transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#334155] dark:bg-transparent dark:text-[#f1f5f9] dark:hover:bg-[#18212f]";

// Field per Figma node 2350:42050: h-[54px], 1px border-strong, rounded-2xl
// (16px corner-radius/lg), p-4, body/default placeholder 16/24 text-tertiary.
// Dark: bg grey-dark-800 #18212f, border #334155, placeholder #94a3b8,
// text #f1f5f9.
const inputClass =
  "block h-[54px] w-full rounded-2xl border border-[#CBD5E1] bg-white p-4 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#1A1A1B] outline-none placeholder:text-[#77869D] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 dark:border-[#334155] dark:bg-[#18212f] dark:text-[#f1f5f9] dark:placeholder:text-[#94a3b8]";

const inputWithIconClass =
  "block h-[54px] w-full rounded-2xl border border-[#CBD5E1] bg-white pl-4 pr-11 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#1A1A1B] outline-none placeholder:text-[#77869D] focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 dark:border-[#334155] dark:bg-[#18212f] dark:text-[#f1f5f9] dark:placeholder:text-[#94a3b8]";

// Primary button per Figma: h-12, rounded-full, 16/24 Rubik Medium white.
// Green since PANDA-1779 — signing in is the encouraged action and the brand
// orange→red sweep read as a warning. Disabled swaps the gradient for a flat
// grey pill: #F1F5F9 bg / #475569 label in light, #18212f / #94A3B8 in dark.
// The label is one step darker (light) and one step lighter (dark) than the
// Figma disabled token, which lands 6.9:1 and 6.3:1 — the token pair itself is
// 2.3:1 / 3.2:1, legible on a mockup and not on a real screen. Nothing else
// says "disabled" here besides the flat fill, so the word has to stay readable.
const primaryBtnClass =
  "flex h-12 w-full items-center justify-center rounded-full bg-panda-gradient-success font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white shadow-md shadow-emerald-900/25 transition-opacity disabled:cursor-not-allowed disabled:bg-none disabled:bg-[#F1F5F9] disabled:text-[#475569] disabled:shadow-none dark:disabled:bg-[#18212f] dark:disabled:text-[#94A3B8]";

// Human-readable name for a Supabase identity provider. Mirrors the helper
// in signup-modal.tsx so both modals label a connected SSO account the same.
function providerLabel(provider: string): string {
  switch (provider) {
    case "google":
      return "Google";
    case "facebook":
      return "Facebook";
    case "linkedin_oidc":
    case "linkedin":
      return "LinkedIn";
    default:
      return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
}

function safeNext(path: string): string {
  if (!path.startsWith("/") || path.startsWith("//")) return "/";
  return path;
}

// The post-login default landing page. `safeNext` returns "/" both for the
// "no explicit destination" sentinel (a plain "Log in") and for rejected
// paths — for a successful login we treat that as "take the user straight to
// their projects dashboard" (login should route directly to /projects). Any
// explicit same-origin deep link (a shared /project/… URL or an invite) is a
// non-"/" path and is preserved as-is.
//
// EXCEPTION: when a home-page action is waiting to resume after auth — a typed
// prompt (home-prompt stash) or a "Clone this" intent (home-clone stash) — the
// login must land back on "/" so the homepage's SIGNED_IN replay can consume
// it. Those flows open this modal with next="/" and carry their payload in
// storage, not in `next`; sending them to /projects would silently drop the
// pending build/clone (both replays are gated on pathname === "/"). Reading the
// stashes here does not consume them — the replay still fires on the homepage.
function loginDestination(path: string): string {
  const safe = safeNext(path);
  if (safe !== "/") return safe;
  if (readHomePromptStash() || readCloneIntent()) return "/";
  return "/projects";
}

// Crossfades between the open-eyes panda and the eyes-covered panda when
// the password field has focus.
function PandaAuthAvatar({ eyesClosed }: { eyesClosed: boolean }) {
  return (
    <div className="relative h-21 w-21 sm:h-20 sm:w-20">
      <Image
        src="/panda-lockup.png"
        alt="Panda"
        width={448}
        height={448}
        priority
        className={cn(
          "absolute inset-0 h-full w-full rounded-full object-cover object-left transition-opacity duration-200 ease-out",
          eyesClosed ? "opacity-0" : "opacity-100",
        )}
      />
      <Image
        src="/eye_close.png"
        alt=""
        width={448}
        height={448}
        priority
        aria-hidden
        className={cn(
          "absolute inset-0 h-full w-full rounded-full object-cover object-center transition-opacity duration-200 ease-out",
          eyesClosed ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}

function EyeToggle({
  visible,
  onToggle,
}: {
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] transition-colors hover:text-[#101010] dark:text-[#94a3b8] dark:hover:text-[#f1f5f9]"
      aria-label={visible ? "Hide password" : "Show password"}
    >
      {visible ? (
        <EyeOff className="h-5 w-5" strokeWidth={1.75} />
      ) : (
        <Eye className="h-5 w-5" strokeWidth={1.75} />
      )}
    </button>
  );
}

type LoginModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where to send the user after successful login. Defaults to "/". */
  next?: string;
  /**
   * Surfaced at the top of the email step when the modal opens after a failed
   * OAuth round-trip (the provider returned an error to /auth/callback). Kept
   * provider-agnostic — never the provider's raw error text.
   */
  initialError?: string | null;
  /**
   * Switches the user into the sign-up flow. When provided, the email step
   * shows a "New to Panda? Sign up" CTA; the parent is responsible for
   * closing this modal and opening the signup modal (preserving `next`).
   *
   * Receives the address typed here (trimmed; "" when the field is empty) so
   * the signup form can pre-fill it — a user we just told "no account found"
   * should not have to retype the email we read it from.
   */
  onSwitchToSignup?: (email: string) => void;
};

type Step = "email" | "password" | "forgot" | "forgot-sent";

export function LoginModal({
  open,
  onOpenChange,
  next = "/",
  initialError,
  onSwitchToSignup,
}: LoginModalProps) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  // The email-step pre-flight confirmed no account exists for this address, so
  // we show a "sign up instead" hint rather than advancing to the "Welcome
  // back" password step. Cleared on edit / back.
  const [noAccount, setNoAccount] = useState(false);
  // Connected sign-in methods for the entered account, from the check-email
  // pre-flight. "email" == a password identity; anything else is an OAuth/SSO
  // provider (e.g. "google"). Drives whether the second step offers the
  // provider's button instead of a password field. Empty when unknown.
  const [oauthProviders, setOauthProviders] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(
    initialError ?? null,
  );
  // Tracks the last `initialError` we mirrored so the sync-during-render below
  // fires once per change (React's recommended alternative to a prop→state
  // effect — see https://react.dev/learn/you-might-not-need-an-effect).
  const [lastInitialError, setLastInitialError] = useState(initialError);
  const [loading, setLoading] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const router = useRouter();

  // Debounced "did you mean?" — recompute the typo hint shortly after the user
  // stops typing, so it is visible BEFORE they click Continue (blur fires too
  // late: handleEmailContinue proceeds before the hint can be acted on). State
  // is set only inside the timeout, keeping this clear of set-state-in-effect.
  useEffect(() => {
    const value = email.trim();
    if (!value) return;
    const id = setTimeout(
      () => setEmailSuggestion(getEmailSuggestion(value)),
      400,
    );
    return () => clearTimeout(id);
  }, [email]);

  // Mirror a fresh OAuth-failure message from the parent into local state the
  // React-blessed way — during render, not in an effect. When the parent hands
  // us a new message (the modal reopened after a failed Google / Facebook /
  // LinkedIn round-trip) we show it and snap to the email step so the social
  // buttons + message are both visible. The parent clears `initialError` on
  // close, which flows through here and clears the banner — so it never
  // lingers into a later manual open.
  if (initialError !== lastInitialError) {
    setLastInitialError(initialError);
    setOauthError(initialError ?? null);
    if (initialError) setStep("email");
  }

  async function handleEmailContinue(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmailStructure(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(null);
    setNoAccount(false);
    // Reset any provider result from a prior email so an unknown / failed
    // lookup falls through to the password step (fail-open), never a stale
    // SSO screen from the previous address.
    setOauthProviders([]);

    // Pre-flight existence check so a brand-new email is never shown the
    // "Welcome back" password step (it read as a returning-user screen to new
    // users — QA report). Only an authoritative "no account" (checked === true)
    // stops here with a sign-up hint. An existing account, an invalid address,
    // or any uncertainty (rate-limit, network, service-role key absent) falls
    // through to the password step, where signInWithPassword stays the real
    // gate — mirrors the signup modal's fail-open philosophy.
    setLoading(true);
    try {
      const res = await fetch("/api/auth/check-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      if (res.ok) {
        const body = (await res.json()) as {
          exists?: boolean;
          checked?: boolean;
          providers?: string[];
        };
        if (body?.checked === true && body?.exists === false) {
          setNoAccount(true);
          setLoading(false);
          return;
        }
        // Carry the account's connected sign-in methods into the second step
        // so an SSO account is offered its provider button, not a password
        // field it may never have set.
        setOauthProviders(Array.isArray(body?.providers) ? body.providers : []);
      } else if (res.status === 400) {
        // The server rejected the address itself — bad structure or a
        // well-shaped but non-existent TLD like "gmial.cob" that the
        // client-side shape check can't catch. Surface it inline and do NOT
        // advance. Other non-ok statuses (429 rate-limit, 5xx) fall through
        // and let signInWithPassword do the gating.
        const errBody = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        if (errBody?.error === "invalid_email") {
          setError("Please enter a valid email address.");
          setLoading(false);
          return;
        }
      }
    } catch {
      // network issue — fall through and let signInWithPassword do the gating
    }
    setLoading(false);
    setStep("password");
  }

  function handleBackToEmail() {
    setStep("email");
    setError(null);
    setEmailSuggestion(null);
    setNoAccount(false);
    setOauthProviders([]);
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const supabase = createClient();
    const { error: signinError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (signinError) {
      setError(signinError.message);
      setLoading(false);
      return;
    }

    onOpenChange(false);
    router.push(loginDestination(next));
    router.refresh();
    setLoading(false);
  }

  // Same server route as the standalone /forgot-password page (PANDA-1323:
  // it builds the recovery link and sends the branded reset email); we just
  // host it inline as a modal step instead of redirecting.
  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setResetLoading(true);
    setResetError(null);

    let failed = false;
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      failed = !res.ok;
    } catch {
      failed = true;
    }

    if (failed) {
      setResetError("Could not send the reset email. Please try again.");
      setResetLoading(false);
      return;
    }

    setStep("forgot-sent");
    setResetLoading(false);
  }

  // An account with connected SSO providers and NO password identity (no
  // "email" provider). We can't sign these users in with a password, so the
  // second step offers their provider button(s) instead of the password
  // field. An account that ALSO has a password ("email" present) keeps the
  // password flow — the combined SSO-plus-password layout is a separate,
  // pending product decision.
  const oauthOnlyProviders =
    oauthProviders.length > 0 && !oauthProviders.includes("email")
      ? oauthProviders
      : [];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 z-60 bg-black/40 backdrop-blur-sm duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 dark:bg-black/50" />
        <DialogPrimitive.Popup
          className={cn(
            // Centered modal at every breakpoint. Mobile keeps 16px outer
            // padding per Figma, desktop bumps to 36px. Dark-mode bg uses
            // background-/-default token (#111522) per Figma 1696:16735.
            "fixed left-1/2 top-1/2 z-70 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-115 -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-3xl bg-white px-4 pb-6 pt-2 shadow-2xl outline-none duration-150 sm:px-9 sm:pb-9 sm:pt-4 dark:bg-[#111522]",
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            fontTight,
          )}
        >
          {/* Top bar (Figma node 2350:42098: h-14, X 24x24 at top-right,
              back arrow 24x24 at top-left when on a sub-step). */}
          <div className="flex h-14 w-full items-center justify-between">
            {step === "password" ? (
              <button
                type="button"
                onClick={handleBackToEmail}
                className="flex h-6 w-6 items-center justify-center rounded-full text-[#1A1A1B] transition-opacity hover:opacity-70 sm:h-9 sm:w-9 sm:hover:bg-zinc-100 dark:text-[#cbd5e1] dark:sm:hover:bg-[#18212f]"
                aria-label="Back"
              >
                <ArrowLeft className="h-6 w-6 sm:h-5 sm:w-5" strokeWidth={2} />
              </button>
            ) : step === "forgot" ? (
              <button
                type="button"
                onClick={() => {
                  setStep("password");
                  setResetError(null);
                }}
                className="flex h-6 w-6 items-center justify-center rounded-full text-[#1A1A1B] transition-opacity hover:opacity-70 sm:h-9 sm:w-9 sm:hover:bg-zinc-100 dark:text-[#cbd5e1] dark:sm:hover:bg-[#18212f]"
                aria-label="Back"
              >
                <ArrowLeft className="h-6 w-6 sm:h-5 sm:w-5" strokeWidth={2} />
              </button>
            ) : (
              <span aria-hidden className="h-6 w-6 sm:h-9 sm:w-9" />
            )}
            <DialogPrimitive.Close
              className="flex h-6 w-6 items-center justify-center rounded-full text-[#1A1A1B] transition-opacity hover:opacity-70 sm:h-9 sm:w-9 sm:hover:bg-zinc-100 dark:text-[#cbd5e1] dark:sm:hover:bg-[#18212f]"
              aria-label="Close"
            >
              <X className="h-6 w-6 sm:h-5 sm:w-5" strokeWidth={2} />
            </DialogPrimitive.Close>
          </div>

          {/* Brand mark — avatar covers eyes when password field is focused.
              On forgot-sent step, swap to the success badge. Figma node
              2350:42382 (light) outer 80x80 #ECFDF3 / inner 40x40 #32D583
              / white tick. Dark (Figma node 1696:36968) flips the outer
              ring to success-/-background #033f26 — pulled from the
              variable defs for the dark "Reset password" frame so the
              ring sits properly against the #111522 modal bg. Inner badge
              and tick stay the same. */}
          <div className="flex w-full justify-center">
            {step === "forgot-sent" ? (
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#ECFDF3] dark:bg-[#033f26]">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#32D583]">
                  <Check className="h-6 w-6 text-white" strokeWidth={2.5} />
                </span>
              </span>
            ) : (
              <PandaAuthAvatar eyesClosed={passwordFocused} />
            )}
          </div>

          {step === "email" ? (
            <>
              <div className="mt-4 flex w-full flex-col items-center gap-1 text-center sm:mt-5 sm:gap-2">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Let&apos;s get started
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  Log in or create an account to start building.
                </DialogPrimitive.Description>
              </div>

              {oauthError ? (
                <div className="mt-4 w-full rounded-xl border border-[#FECDCA] bg-[#FEF3F2] px-4 py-3 sm:mt-5 dark:border-[#7f1d1d] dark:bg-[#2a1414]">
                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#B42318] dark:text-[#fda4af]">
                    {oauthError}
                  </p>
                </div>
              ) : null}

              <div className="mt-5 flex w-full flex-col gap-3 sm:mt-6">
                <GoogleSignInButton
                  label="Continue with Google"
                  buttonClassName={socialBtnClass}
                  mode="login"
                  next={loginDestination(next)}
                />
                <FacebookSignInButton
                  label="Continue with Facebook"
                  buttonClassName={socialBtnClass}
                  mode="login"
                  next={loginDestination(next)}
                />
                <LinkedInSignInButton
                  label="Continue with LinkedIn"
                  buttonClassName={socialBtnClass}
                  next={loginDestination(next)}
                />
              </div>

              <div className="mt-5 flex w-full items-center gap-3 sm:mt-6">
                <div className="min-h-0 min-w-0 flex-1 border-t border-[#CBD5E1] dark:border-[#334155] sm:border-[#E5E7EB] sm:dark:border-[#334155]" />
                <span className="shrink-0 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[13px] sm:leading-normal sm:text-[#9CA3AF] sm:dark:text-[#94a3b8]">
                  or
                </span>
                <div className="min-h-0 min-w-0 flex-1 border-t border-[#CBD5E1] dark:border-[#334155] sm:border-[#E5E7EB] sm:dark:border-[#334155]" />
              </div>

              <form
                onSubmit={handleEmailContinue}
                className="mt-5 flex w-full flex-col gap-4"
              >
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                    if (oauthError) setOauthError(null);
                    if (noAccount) setNoAccount(false);
                    // Editing invalidates any prior "did you mean" hint; it's
                    // recomputed on blur.
                    if (emailSuggestion) setEmailSuggestion(null);
                  }}
                  className={inputClass}
                  required
                />

                {error ? (
                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#dc2626] dark:text-[#fda4af]">{error}</p>
                ) : noAccount ? (
                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8]">
                    No account found for this email.{" "}
                    {onSwitchToSignup ? (
                      <button
                        type="button"
                        onClick={() => onSwitchToSignup(email.trim())}
                        className="font-medium text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                      >
                        Sign up
                      </button>
                    ) : null}
                  </p>
                ) : emailSuggestion ? (
                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8]">
                    Did you mean{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setEmail(emailSuggestion);
                        setEmailSuggestion(null);
                      }}
                      className="font-medium text-emerald-700 underline underline-offset-2 transition-colors hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className={primaryBtnClass}
                >
                  {loading ? "Checking…" : "Continue"}
                </button>
              </form>

              {/* New-user CTA — mirrors the inline "Remember password? Log in"
                  switch style (label #77869D + brand link #B90C3B). Only shown
                  when the parent wires up a way to reach the signup flow. */}
              {onSwitchToSignup ? (
                <div className="mt-4 flex w-full items-center justify-center gap-1.5">
                  <span className="font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[14px] sm:leading-5 sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                    New to Panda?
                  </span>
                  <button
                    type="button"
                    onClick={() => onSwitchToSignup(email.trim())}
                    className="font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-emerald-700 hover:opacity-70 dark:text-emerald-400 sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[14px] sm:font-semibold sm:leading-5 sm:text-emerald-700 sm:hover:text-emerald-800 sm:hover:opacity-100 sm:dark:text-emerald-400"
                  >
                    Sign up
                  </button>
                </div>
              ) : null}

              {/* Terms & Privacy footer — Figma node 1696:16752. Links share
                  the muted footer color/weight (text-tertiary #94a3b8 dark),
                  no distinct emphasis. Light mirrors the same role tokens. */}
              <p className="mt-2 w-full text-center font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8]">
                By continuing, you have read and agree to our{" "}
                <a
                  href="/terms"
                  className="font-normal text-[#77869D] underline-offset-2 hover:underline dark:text-[#94a3b8]"
                >
                  Terms and Conditions
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  className="font-normal text-[#77869D] underline-offset-2 hover:underline dark:text-[#94a3b8]"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </>
          ) : step === "password" && oauthOnlyProviders.length > 0 ? (
            <>
              <div className="mt-4 flex w-full flex-col items-center gap-1 text-center sm:mt-5 sm:gap-2">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Welcome back
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  {oauthOnlyProviders.length === 1 ? (
                    <>
                      You signed up with {providerLabel(oauthOnlyProviders[0])}.
                      Continue to sign in as{" "}
                    </>
                  ) : (
                    <>
                      You signed up with a connected account. Continue to sign
                      in as{" "}
                    </>
                  )}
                  <span className="font-medium text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-semibold sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                    {email}
                  </span>
                  .
                </DialogPrimitive.Description>
              </div>

              <div className="mt-5 flex w-full flex-col gap-3 sm:mt-6">
                {oauthOnlyProviders.map((p) => {
                  if (p === "google") {
                    return (
                      <GoogleSignInButton
                        key={p}
                        label="Continue with Google"
                        buttonClassName={socialBtnClass}
                        mode="login"
                        next={loginDestination(next)}
                      />
                    );
                  }
                  if (p === "facebook") {
                    return (
                      <FacebookSignInButton
                        key={p}
                        label="Continue with Facebook"
                        buttonClassName={socialBtnClass}
                        mode="login"
                        next={loginDestination(next)}
                      />
                    );
                  }
                  if (p === "linkedin_oidc" || p === "linkedin") {
                    return (
                      <LinkedInSignInButton
                        key={p}
                        label="Continue with LinkedIn"
                        buttonClassName={socialBtnClass}
                        next={loginDestination(next)}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            </>
          ) : step === "password" ? (
            <>
              <div className="mt-4 flex w-full flex-col items-center gap-1 text-center sm:mt-5 sm:gap-2">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Welcome back
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  Enter your password and login as{" "}
                  <span className="font-medium text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-semibold sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                    {email}
                  </span>
                  .
                </DialogPrimitive.Description>
              </div>

              <form
                onSubmit={handleLogin}
                className="mt-5 flex w-full flex-col gap-3 sm:mt-6 sm:gap-4"
              >
                <div className="relative w-full">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    className={inputWithIconClass}
                    required
                  />
                  <EyeToggle
                    visible={showPassword}
                    onToggle={() => setShowPassword((v) => !v)}
                  />
                </div>

                {error ? (
                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#dc2626] dark:text-[#fda4af]">{error}</p>
                ) : null}

                <button
                  type="submit"
                  disabled={loading || !password}
                  className={primaryBtnClass}
                >
                  {loading ? "Signing in…" : "Log in"}
                </button>

                {/* Forgot password — Figma node 2350:42286: full-width
                    tertiary button, h-12 with 16/24 Rubik Medium. Light
                    uses #B90C3B (button-/-tertiary-/-text-/-default light);
                    dark uses #f23326 (button-/-tertiary-/-text-/-default
                    dark, from get_variable_defs on node 1696:31708). */}
                <button
                  type="button"
                  onClick={() => {
                    setStep("forgot");
                    setError(null);
                    setResetError(null);
                  }}
                  className="flex h-12 w-full items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-emerald-700 hover:opacity-70 dark:text-emerald-400 sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:font-semibold sm:text-emerald-700 sm:hover:text-emerald-800 sm:hover:opacity-100 sm:dark:text-emerald-400"
                >
                  Forgot password?
                </button>
              </form>
            </>
          ) : null}

          {step === "forgot" ? (
            <>
              {/* Title + description per Figma node 2350:42310 — title is
                  "Reset password?" (not "Forgot password?") and CTA reads
                  "Reset password". Body 16/24 Rubik #77869D on mobile,
                  14px Inter Tight on desktop. */}
              <div className="mt-4 flex w-full flex-col items-center gap-1 text-center sm:mt-5 sm:gap-2">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Forgot password?
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[14px] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  We&apos;ll send you a secure link to create a password to{" "}
                  <span className="font-medium text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-semibold sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                    {email}
                  </span>
                  .
                </DialogPrimitive.Description>
              </div>

              <form
                onSubmit={handleResetPassword}
                className="mt-5 flex w-full flex-col gap-3 sm:mt-6 sm:gap-4"
              >
                {resetError ? (
                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#dc2626] dark:text-[#fda4af]">{resetError}</p>
                ) : null}

                <button
                  type="submit"
                  disabled={resetLoading}
                  className={primaryBtnClass}
                >
                  {resetLoading ? "Sending…" : "Send email"}
                </button>

                {/* Inline "Remember password? Log in" — Figma node
                    2350:42348: 16/24 Rubik, label #77869D, link #B90C3B
                    (button-/-tertiary-/-text-/-default). */}
                <div className="flex w-full items-center justify-center gap-1.5">
                  <span className="font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[14px] sm:leading-5 sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                    Remember password?
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("password");
                      setResetError(null);
                    }}
                    className="font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-emerald-700 hover:opacity-70 dark:text-emerald-400 sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[14px] sm:font-semibold sm:leading-5 sm:text-emerald-700 sm:hover:text-emerald-800 sm:hover:opacity-100 sm:dark:text-emerald-400"
                  >
                    Log in
                  </button>
                </div>
              </form>
            </>
          ) : null}

          {step === "forgot-sent" ? (
            <>
              {/* Title + body per Figma node 2350:42385: title 24/32
                  Rubik Medium #1A1A1B; two body paragraphs 16/24 Rubik
                  #77869D with 16px gap between them (Figma description
                  spans 108px tall = ~5 lines + paragraph gap). */}
              <div className="mt-4 flex w-full flex-col items-center gap-4 text-center sm:mt-5 sm:gap-3">
                <DialogPrimitive.Title className="font-[family-name:var(--font-rubik),sans-serif] text-[24px] font-medium leading-8 text-[#1A1A1B] dark:text-[#f1f5f9] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[28px] sm:font-bold sm:leading-[1.2] sm:text-[#101010] sm:dark:text-[#f1f5f9]">
                  Check your email
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[14px] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  We&apos;ve sent an email to your account. Click the link in
                  the email to reset your password.
                </DialogPrimitive.Description>
                <p className="font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#77869D] dark:text-[#94a3b8] sm:font-[family-name:var(--font-inter-tight),sans-serif] sm:text-[14px] sm:leading-normal sm:text-[#6B7280] sm:dark:text-[#94a3b8]">
                  If you don&apos;t see the email, please check your junk,
                  spam, social, or other folders.
                </p>
              </div>

              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setStep("password");
                    setResetError(null);
                    setPassword("");
                    setError(null);
                  }}
                  className={primaryBtnClass}
                >
                  Continue to log in
                </button>
              </div>
            </>
          ) : null}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
